import { Tracer } from './tracer';
import { volumeRule } from './rules/volume';
import { frequencyRule } from './rules/frequency';
import { splitRule } from './rules/split';
import { exerciseSelectionRule } from './rules/exerciseSelection';
import { loadPrescriptionRule, applyPrescription } from './rules/loadPrescription';
import { progressionRule } from './rules/progression';
import { deloadRule } from './rules/deload';
import { FIXTURE_EXERCISES, isCompound } from './fixtures/exercises';
import type {
  ClientInput,
  Exercise,
  GenerateOptions,
  Plan,
  PlanDay,
  PlanWeek,
  RuleTraceEntry,
  Split,
  PrescribedExercise,
  MuscleGroup,
} from './types';

export interface GenerateResult {
  plan: Plan;
  trace: RuleTraceEntry[];
}

export interface GenerateInput {
  client: ClientInput;
  options: GenerateOptions;
  /** Optional override for the exercise pool. Defaults to FIXTURE_EXERCISES. */
  exercises?: Exercise[];
}

/**
 * Orchestrate the rule pipeline:
 *   1. volume → 2. frequency → 3. split → 4. exerciseSelection
 *   5. build each week's days using load prescription
 *   6. progression (forward projection)
 *   7. deload (every 4th week)
 *
 * Returns the structured plan and the rule trace.
 */
export function generatePlan(input: GenerateInput): GenerateResult {
  const trace = new Tracer();
  const { client, options } = input;
  const exercises = input.exercises ?? FIXTURE_EXERCISES;

  const volume = volumeRule(client, trace);
  const frequency = frequencyRule(client, trace);
  const split = splitRule(client, trace);
  const exercisePool = exerciseSelectionRule(client, exercises, trace);
  const prescribe = loadPrescriptionRule(client, trace);

  const weeks: PlanWeek[] = [];
  for (let weekIdx = 1; weekIdx <= options.weeks; weekIdx++) {
    const days = buildWeekDays(
      weekIdx,
      client.schedule.days_per_week,
      client.schedule.session_duration_min,
      split,
      exercisePool,
      prescribe,
    );
    weeks.push({ week_number: weekIdx, is_deload: false, days });
  }

  progressionRule(weeks, trace);
  deloadRule(weeks, trace);

  const startDate = new Date(options.start_date);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + options.weeks * 7 - 1);

  const plan: Plan = {
    client_id: client.id,
    weeks,
    split,
    start_date: options.start_date,
    end_date: endDate.toISOString().slice(0, 10),
    generated_by: 'rule_engine_v1',
  };

  // _volume reserved for diagnostics; suppressing unused-warning at runtime.
  void volume;
  void frequency;

  return { plan, trace: trace.snapshot() };
}

// =============================================================================
// Day-building helpers
// =============================================================================

/**
 * Map split → an ordered list of "focus" labels per day of the week.
 */
function focusForDay(split: Split, dayIdx: number, totalDays: number): string {
  if (split === 'full_body') return 'full body';
  if (split === 'upper_lower') {
    // alternate U/L across the week
    return dayIdx % 2 === 0 ? 'upper' : 'lower';
  }
  // push_pull_legs across 5–6 days
  const labels = ['push', 'pull', 'legs', 'push', 'pull', 'legs'];
  void totalDays;
  return labels[dayIdx] ?? 'full body';
}

/**
 * For a given focus + pool, pick exercises that cover the required movement
 * patterns and primary muscles. Returns an ordered list ready for prescription.
 */
function selectExercisesForFocus(
  focus: string,
  pool: Exercise[],
): Exercise[] {
  const patterns: Record<string, string[]> = {
    'full body': ['squat', 'hinge', 'push', 'pull', 'core'],
    upper: ['push', 'pull', 'push', 'pull'],
    lower: ['squat', 'hinge', 'squat', 'core'],
    push: ['push', 'push', 'push', 'core'],
    pull: ['pull', 'pull', 'pull'],
    legs: ['squat', 'hinge', 'squat'],
  };

  const required = patterns[focus] ?? ['push', 'pull'];
  const used = new Set<string>();
  const picks: Exercise[] = [];

  for (const pattern of required) {
    // Prefer compound > isolation; prefer not-yet-used.
    const candidate =
      pool.find((e) => e.movement_pattern === pattern && !used.has(e.id) && isCompound(e)) ??
      pool.find((e) => e.movement_pattern === pattern && !used.has(e.id));
    if (candidate) {
      picks.push(candidate);
      used.add(candidate.id);
    }
  }

  return picks;
}

function buildWeekDays(
  _weekIdx: number,
  daysPerWeek: number,
  sessionDurationMin: number,
  split: Split,
  pool: Exercise[],
  prescribe: ReturnType<typeof loadPrescriptionRule>,
): PlanDay[] {
  const days: PlanDay[] = [];

  for (let d = 0; d < daysPerWeek; d++) {
    const focus = focusForDay(split, d, daysPerWeek);
    const picks = selectExercisesForFocus(focus, pool);

    const exercises: PrescribedExercise[] = picks.map((ex, idx) =>
      applyPrescription(
        ex.id,
        ex.name,
        ex.primary_muscle as MuscleGroup,
        ex.movement_pattern,
        idx,
        isCompound(ex),
        prescribe,
      ),
    );

    days.push({
      day_number: d + 1,
      focus,
      target_duration_min: sessionDurationMin,
      exercises,
    });
  }

  return days;
}
